import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Bell, AlertTriangle, X, CheckCheck, Check, Settings as SettingsIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import {
  fetchNotifications,
  fetchUnreadCount,
  markAsRead,
  markAllAsRead,
} from "@/lib/notifications";
import type { Notification } from "@/lib/types";
import { formatDistanceToNow } from "date-fns";
import { Link } from "@tanstack/react-router";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

export function NotificationBell() {
  const { profile } = useAuth();
  const isStaff = profile?.role === "staff" || profile?.role === "admin";
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!profile?.id) return;
    try {
      const [notifs, count] = await Promise.all([
        fetchNotifications(profile.id, 20),
        fetchUnreadCount(profile.id),
      ]);
      setNotifications(notifs);
      setUnreadCount(count);
    } catch (err) {
      console.error("Error loading notifications:", err);
    }
  }, [profile?.id]);

  useEffect(() => {
    if (!profile?.id) return;
    setLoading(true);
    reload().finally(() => setLoading(false));

    // Live updates so the unread badge reflects new/updated notifications
    // without requiring a page refresh.
    const channel = supabase
      .channel(`notifications:${profile.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${profile.id}`,
        },
        () => {
          void reload();
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [profile?.id, reload]);

  const handleOpen = () => setIsOpen(true);

  const handleClose = () => setIsOpen(false);

  const handleMarkAllRead = async () => {
    if (!profile?.id || unreadCount === 0) return;
    // Optimistic update first so the UI feels instant
    const snapshot = notifications;
    const prevCount = unreadCount;
    setUnreadCount(0);
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, is_read: true, read_at: new Date().toISOString() })),
    );
    try {
      await markAllAsRead(profile.id);
    } catch (err) {
      console.error("markAllAsRead failed:", err);
      // Roll back optimistic update on failure
      setNotifications(snapshot);
      setUnreadCount(prevCount);
    } finally {
      // Re-sync from Supabase so the list matches server truth
      void reload();
    }
  };

  const markOne = useCallback(
    async (id: string) => {
      // Optimistic update so the row updates immediately
      let wasUnread = false;
      setNotifications((prev) =>
        prev.map((n) => {
          if (n.id !== id) return n;
          if (!n.is_read) wasUnread = true;
          return { ...n, is_read: true, read_at: new Date().toISOString() };
        }),
      );
      if (wasUnread) setUnreadCount((prev) => Math.max(0, prev - 1));
      try {
        await markAsRead(id);
      } catch (err) {
        console.error("markAsRead failed:", err);
      } finally {
        // Re-sync from Supabase to stay consistent
        void reload();
      }
    },
    [reload],
  );

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.is_read) {
      await markOne(notification.id);
    }
    handleClose();
  };

  if (!profile) return null;

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="relative rounded-full"
        onClick={handleOpen}
        aria-label="Open notifications"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </Button>

      {createPortal(
        <AnimatePresence>
          {isOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                key="backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm"
                onClick={handleClose}
              />

              {/* Panel — full-height sidebar on md+, bottom sheet on mobile */}
              <motion.div
                key="panel"
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 28, stiffness: 300 }}
                className="fixed right-0 top-0 z-50 flex h-full w-full flex-col border-l border-border bg-background shadow-2xl md:w-96"
              >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-border px-5 py-4">
                  <div className="flex items-center gap-2">
                    <Bell className="h-4 w-4 text-muted-foreground" />
                    <h2 className="font-semibold">Notifications</h2>
                    {unreadCount > 0 && (
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                        {unreadCount} new
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {unreadCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 gap-1.5 rounded-full text-xs text-muted-foreground hover:text-foreground"
                        onClick={handleMarkAllRead}
                      >
                        <CheckCheck className="h-3.5 w-3.5" />
                        Mark all read
                      </Button>
                    )}
                    <Link
                      to="/app/notifications"
                      onClick={handleClose}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                      aria-label="Notification preferences"
                      title="Notification preferences"
                    >
                      <SettingsIcon className="h-3.5 w-3.5" />
                    </Link>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-full"
                      onClick={handleClose}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-3">
                  {loading ? (
                    <div className="flex flex-col gap-2 py-4">
                      {[...Array(4)].map((_, i) => (
                        <div key={i} className="h-16 animate-pulse rounded-xl bg-muted" />
                      ))}
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                      <Bell className="mb-3 h-8 w-8 text-muted-foreground/40" />
                      <p className="text-sm font-medium text-muted-foreground">All caught up</p>
                      <p className="mt-1 text-xs text-muted-foreground/70">No notifications yet</p>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {notifications.map((notification) => (
                        <NotificationItem
                          key={notification.id}
                          notification={notification}
                          onClick={() => handleNotificationClick(notification)}
                          onMarkRead={() => markOne(notification.id)}
                          isStaff={isStaff}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
}

function NotificationItem({
  notification,
  onClick,
  onMarkRead,
  isStaff,
}: {
  notification: Notification;
  onClick: () => void;
  onMarkRead: () => void | Promise<void>;
  isStaff: boolean;
}) {
  const relativeTime = formatDistanceToNow(new Date(notification.created_at), { addSuffix: true });
  const isOnHold = notification.type === "job_on_hold";
  const jobId = notification.data?.job_id;

  const inner = (
    <div
      className={cn(
        "group flex gap-3 rounded-xl px-3 py-3 transition-colors hover:bg-muted cursor-pointer",
        !notification.is_read && "bg-primary/5",
      )}
    >
      <div
        className={cn(
          "mt-0.5 h-2 w-2 shrink-0 rounded-full",
          !notification.is_read ? "bg-primary" : "bg-transparent",
        )}
      />
      <div className="flex-1 min-w-0">
        {isOnHold && (
          <div className="mb-1 flex items-center gap-1 text-xs font-medium text-amber-600">
            <AlertTriangle className="h-3 w-3" /> On Hold
          </div>
        )}
        <div className={cn("text-sm leading-snug", !notification.is_read && "font-semibold")}>
          {notification.title}
        </div>
        {notification.message && (
          <div className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
            {notification.message}
          </div>
        )}
        <div className="mt-1.5 text-[10px] text-muted-foreground/70">{relativeTime}</div>
      </div>
      {!notification.is_read && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void onMarkRead();
          }}
          className="shrink-0 self-start rounded-full p-1.5 text-muted-foreground opacity-0 transition-opacity hover:bg-background hover:text-foreground group-hover:opacity-100 focus:opacity-100"
          aria-label="Mark as read"
          title="Mark as read"
        >
          <Check className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );

  if (jobId) {
    return isStaff ? (
      <Link to="/app/staff/jobs/$id" params={{ id: jobId }} onClick={onClick} className="block">
        {inner}
      </Link>
    ) : (
      <Link to="/app/customer/jobs/$id" params={{ id: jobId }} onClick={onClick} className="block">
        {inner}
      </Link>
    );
  }
  return <div onClick={onClick}>{inner}</div>;
}
